import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { websiteImages } from '../config/images';
import { X, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import ReviewsMarquee from './ReviewsMarquee';

const GallerySection = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [index, setIndex] = useState(null);
  const touchStartX = useRef(null);

  const images = websiteImages.gallery;
  const isOpen = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback((e) => {
    if (e) e.stopPropagation();
    setIndex((i) => (i === null ? i : (i - 1 + images.length) % images.length));
  }, [images.length]);
  const next = useCallback((e) => {
    if (e) e.stopPropagation();
    setIndex((i) => (i === null ? i : (i + 1) % images.length));
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close, prev, next]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (delta > 50) prev();
    else if (delta < -50) next();
  };

  return (
    <section id="gallery" className="py-20 bg-gradient-to-b from-gray-900 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Crimson Text', serif" }}>
            {t.gallery_title}
          </h2>
          <p className="text-xl text-white/80 mb-6">
            {t.gallery_subtitle}
          </p>
          {/* Gold customer-photos button right under the title */}
          {/* Desktop link */}
          <a
            href="https://maps.app.goo.gl/Sccu3gjHYFVVPJ8V9"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gallery-google-photos-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-[#d4af37] text-black px-8 py-4 rounded-lg font-bold hover:bg-[#f0c24d] transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            <ExternalLink className="w-5 h-5" />
            {t.gallery_view_reviews}
          </a>
          {/* Mobile link */}
          <a
            href="https://maps.app.goo.gl/N9JbqB9fBYkd3Syj8?g_st=ic"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gallery-google-photos-btn-mobile"
            className="inline-flex sm:hidden items-center gap-2 bg-[#d4af37] text-black px-8 py-4 rounded-lg font-bold hover:bg-[#f0c24d] transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            <ExternalLink className="w-5 h-5" />
            {t.gallery_view_reviews}
          </a>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, i) => (
            <div 
              key={i} 
              data-testid={`gallery-thumb-${i}`}
              className="group relative aspect-square overflow-hidden rounded-xl cursor-pointer shadow-lg"
              onClick={() => setIndex(i)}
            >
              <img 
                src={image.url} 
                alt={image.alt} 
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          ))}
        </div>

        {/* Google Reviews marquee */}
        <ReviewsMarquee />
      </div>

      {/* Lightbox with navigation + swipe */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[80] bg-black flex items-center justify-center p-4 select-none"
          onClick={close}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          data-testid="gallery-lightbox"
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-[#d4af37] transition-colors duration-200 z-10 bg-white/10 rounded-full p-2"
            onClick={(e) => { e.stopPropagation(); close(); }}
            data-testid="lightbox-close"
            aria-label="Close"
          >
            <X className="w-7 h-7" />
          </button>

          {/* Prev */}
          <button
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-[#d4af37] hover:text-black rounded-full p-2 md:p-3 transition-all duration-200 z-10"
            onClick={prev}
            data-testid="lightbox-prev"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
          </button>

          {/* Next */}
          <button
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-[#d4af37] hover:text-black rounded-full p-2 md:p-3 transition-all duration-200 z-10"
            onClick={next}
            data-testid="lightbox-next"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
          </button>

          <img 
            key={index}
            src={images[index].url} 
            alt={images[index].alt} 
            className="max-w-full max-h-[85vh] object-contain rounded-lg reveal-up"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />

          {/* Counter */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-white/10 rounded-full px-4 py-1">
            {index + 1} / {images.length}
          </div>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
